"use client";

import Image from "next/image";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import CtaGridBackground from "../../components/CtaGridBackground";
import { useMedia, FadeInFast } from "../../lib/hooks";
import { BG, BG_ALT, TEXT, TEXT_MUTED, TEAL, CARD_BORDER, MAX_W, utm } from "../../components/marketing/tokens";
import { PrimaryBtn, SecondaryBtn } from "../../components/marketing/buttons";
import { StatusChip } from "./badges";
import CodeBlock from "../../blog/components/CodeBlock";
import { MOCKUPS } from "./mockups";
import { DirectoryCard } from "./cards";
import FaqList from "./FaqList";
import { getIntegrationBySlug } from "../data";

/* Detail page = product doc, not sales page (the Linear register):
   breadcrumb, outcome headline + ONE teal CTA, product visual, overview,
   capabilities, setup narrative, data and permissions, FAQs, related. */

const PROSE_W = 760;

function SectionHeading({ children }) {
  return (
    <h2 style={{
      fontFamily: "var(--display)", fontWeight: 600,
      fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.018em",
      color: TEXT, margin: "0 0 20px",
    }}>{children}</h2>
  );
}

function Prose({ children, style = {} }) {
  return (
    <p style={{
      fontFamily: "var(--body)", fontSize: 16, color: TEXT_MUTED,
      lineHeight: 1.75, margin: "0 0 18px", ...style,
    }}>{children}</p>
  );
}

export default function IntegrationDetailContent({ integration, detail }) {
  const isMobile = useMedia("(max-width: 768px)");
  const Mockup = MOCKUPS[detail.mockupSlug || integration.slug];
  const related = (detail.related || [])
    .map((slug) => getIntegrationBySlug(slug))
    .filter(Boolean);
  const isLaunchingSoon = integration.status === "launching-soon";
  const dp = detail.dataAndPermissions;

  return (
    <div style={{ background: BG, minHeight: "100vh" }}>
      <Nav />
      <main>
        {/* Breadcrumb + hero */}
        <section style={{ background: BG, paddingTop: isMobile ? 100 : 130, paddingBottom: isMobile ? 32 : 48 }}>
          <div style={{ maxWidth: MAX_W, margin: "0 auto", padding: "0 24px" }}>
            <FadeInFast>
              <nav aria-label="Breadcrumb" style={{
                fontFamily: "var(--body)", fontSize: 13, color: TEXT_MUTED,
                marginBottom: 32, display: "flex", alignItems: "center", gap: 8,
              }}>
                <a href="/integrations" style={{ color: TEXT_MUTED, textDecoration: "none" }}
                  onMouseOver={(e) => e.currentTarget.style.color = TEXT}
                  onMouseOut={(e) => e.currentTarget.style.color = TEXT_MUTED}
                >Integrations</a>
                <span style={{ opacity: 0.5 }}>/</span>
                <span style={{ color: TEXT }}>{integration.name}</span>
              </nav>
            </FadeInFast>

            <FadeInFast delay={0.04}>
              {/* Dual-logo lockup */}
              <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: "rgba(255,255,255,0.04)", border: `1px solid rgba(255,255,255,0.08)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative", zIndex: 1,
                }}>
                  <Image src={integration.icon} alt={`${integration.name} logo`} width={30} height={30} />
                </div>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginLeft: -10, fontSize: 24, color: TEAL, fontWeight: 700,
                }} aria-label="AI-Advisors">
                  ✦
                </div>
              </div>

              <h1 style={{
                fontFamily: "var(--display)", fontWeight: 700,
                fontSize: "clamp(28px, 3.6vw, 44px)", letterSpacing: "-0.027em",
                color: TEXT, lineHeight: 1.15, margin: "0 0 14px", maxWidth: 720,
              }}>{detail.outcomeHeadline}</h1>

              <div style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED, marginBottom: 18,
              }}>
                <span>By AI-Advisors</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{integration.category}</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{integration.plan}</span>
                {isLaunchingSoon && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <StatusChip status="launching-soon" />
                  </>
                )}
              </div>

              <p style={{
                fontFamily: "var(--body)", fontSize: isMobile ? 16 : 18, color: TEXT_MUTED,
                lineHeight: 1.7, margin: "0 0 28px", maxWidth: 640,
              }}>{detail.heroIntro}</p>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <PrimaryBtn href={detail.cta?.href || utm("integrations", "connect", `${integration.slug}_detail`)}>
                  {detail.cta?.label || `Connect ${integration.name}`}
                </PrimaryBtn>
                <SecondaryBtn href="/integrations">View all integrations</SecondaryBtn>
              </div>
            </FadeInFast>
          </div>
        </section>

        {/* Product visual */}
        {Mockup && (
          <section style={{ background: BG, padding: isMobile ? "8px 20px 48px" : "16px 24px 72px" }}>
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <FadeInFast>
                <Mockup />
              </FadeInFast>
            </div>
          </section>
        )}

        {/* Overview */}
        <section style={{ background: BG_ALT, padding: isMobile ? "56px 20px" : "80px 24px" }}>
          <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
            <FadeInFast>
              <SectionHeading>Overview</SectionHeading>
              {detail.overview.map((para, i) => (
                <Prose key={i} style={i === detail.overview.length - 1 ? { marginBottom: 0 } : {}}>{para}</Prose>
              ))}
            </FadeInFast>
          </div>
        </section>

        {/* How it works */}
        <section style={{ background: BG, padding: isMobile ? "56px 20px" : "80px 24px" }}>
          <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
            <FadeInFast>
              <SectionHeading>How it works</SectionHeading>
            </FadeInFast>
            {detail.capabilities.map((cap, i) => (
              <FadeInFast key={cap.title} delay={Math.min(i * 0.04, 0.16)}>
                <div style={{ marginBottom: i < detail.capabilities.length - 1 ? 28 : 0 }}>
                  <h3 style={{
                    fontFamily: "var(--display)", fontSize: 17, fontWeight: 600,
                    color: TEXT, margin: "0 0 8px", letterSpacing: "-0.012em",
                  }}>{cap.title}</h3>
                  <Prose style={{ marginBottom: 0 }}>{cap.detail}</Prose>
                </div>
              </FadeInFast>
            ))}
          </div>
        </section>

        {/* Real product screenshots. Optional per integration, and deliberately
            separate from the Mockup above: the mockup is the page's one
            stylised moment, these are photographs of the thing running. Wider
            than PROSE_W because a 1600x900 screen squeezed into prose width is
            unreadable. */}
        {detail.screenshots && detail.screenshots.items?.length > 0 && (
          <section style={{ background: BG_ALT, padding: isMobile ? "56px 20px" : "80px 24px" }}>
            <div style={{ maxWidth: 940, margin: "0 auto" }}>
              <FadeInFast>
                <SectionHeading>{detail.screenshots.heading || "See it in action"}</SectionHeading>
              </FadeInFast>
              {detail.screenshots.items.map((shot, i) => (
                <FadeInFast key={shot.src} delay={Math.min(i * 0.06, 0.18)}>
                  <figure style={{ margin: i === 0 ? "0 0 36px" : "0 0 36px" }}>
                    <div style={{
                      borderRadius: 12, overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.08)",
                      lineHeight: 0,
                    }}>
                      <Image
                        src={shot.src}
                        alt={shot.alt}
                        width={1600}
                        height={900}
                        sizes="(max-width: 940px) 100vw, 940px"
                        style={{ width: "100%", height: "auto" }}
                      />
                    </div>
                    {shot.caption && (
                      <figcaption style={{
                        fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED,
                        lineHeight: 1.6, margin: "12px 2px 0",
                      }}>{shot.caption}</figcaption>
                    )}
                  </figure>
                </FadeInFast>
              ))}
              {detail.screenshots.note && (
                <FadeInFast>
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 13, color: TEXT_MUTED,
                    opacity: 0.85, margin: "4px 2px 0",
                  }}>{detail.screenshots.note}</p>
                </FadeInFast>
              )}
            </div>
          </section>
        )}

        {/* Set up */}
        <section style={{ background: BG_ALT, padding: isMobile ? "56px 20px" : "80px 24px" }}>
          <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
            <FadeInFast>
              <SectionHeading>{isLaunchingSoon ? "How setup will work" : "Set up"}</SectionHeading>
              {detail.setup.intro && <Prose>{detail.setup.intro}</Prose>}
              {detail.setup.steps.map((para, i) => (
                <Prose key={i}>{para}</Prose>
              ))}
              {detail.setup.time && (
                <p style={{
                  fontFamily: "var(--mono)", fontSize: 13, color: TEXT_MUTED,
                  margin: "24px 0 0",
                }}>{detail.setup.time}</p>
              )}
              {/* Optional copy-paste client config snippets (added 2026-08-09
                  for the MCP detail page). Each: { label, syntax, code }. */}
              {detail.setup.snippets && detail.setup.snippets.length > 0 && (
                <div style={{ marginTop: 28 }}>
                  {detail.setup.snippets.map((s) => (
                    <div key={s.label} style={{ marginBottom: 18 }}>
                      <p style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 700, color: TEXT, margin: "0 0 8px" }}>{s.label}</p>
                      <CodeBlock lang={s.label} syntax={s.syntax}>{s.code}</CodeBlock>
                    </div>
                  ))}
                </div>
              )}
            </FadeInFast>
          </div>
        </section>

        {/* First prompts to try (optional; added 2026-08-09 for MCP) */}
        {detail.tryPrompts && (
          <section style={{ background: BG, padding: isMobile ? "56px 20px" : "80px 24px" }}>
            <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
              <FadeInFast>
                <SectionHeading>First prompts to try</SectionHeading>
                {detail.tryPrompts.intro && <Prose>{detail.tryPrompts.intro}</Prose>}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
                  {detail.tryPrompts.prompts.map((p) => (
                    <div key={p} style={{
                      border: `1px solid ${CARD_BORDER}`, borderRadius: 10, background: "#0f0f11",
                      padding: "12px 16px", fontFamily: "var(--mono)", fontSize: 13.5,
                      color: TEXT, lineHeight: 1.6,
                    }}>{p}</div>
                  ))}
                </div>
              </FadeInFast>
            </div>
          </section>
        )}

        {/* Data and permissions */}
        {dp && (
          <section style={{ background: BG, padding: isMobile ? "56px 20px" : "80px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeInFast>
                <div style={{ maxWidth: PROSE_W, margin: "0 auto 28px" }}>
                  <SectionHeading>Data and permissions</SectionHeading>
                  {dp.intro && <Prose style={{ marginBottom: 0 }}>{dp.intro}</Prose>}
                </div>
              </FadeInFast>
              <FadeInFast delay={0.06}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap: 14, maxWidth: PROSE_W, margin: "0 auto",
                }}>
                  {[
                    { title: "What we read", items: dp.reads },
                    { title: "What we write", items: dp.writes },
                    { title: "What we never touch", items: dp.neverTouches },
                  ].map((col) => (
                    <div key={col.title} style={{
                      border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 18px",
                      background: "rgba(255,255,255,0.015)",
                    }}>
                      <div style={{
                        fontFamily: "var(--display)", fontSize: 14, fontWeight: 600,
                        color: TEXT, marginBottom: 12,
                      }}>{col.title}</div>
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {(col.items && col.items.length > 0 ? col.items : ["Nothing"]).map((item, i) => (
                          <li key={i} style={{
                            fontFamily: "var(--body)", fontSize: 13.5, color: TEXT_MUTED,
                            lineHeight: 1.55, padding: "5px 0 5px 16px", position: "relative",
                          }}>
                            <span style={{
                              position: "absolute", left: 0, top: 12,
                              width: 5, height: 5, borderRadius: "50%",
                              background: "rgba(79,205,196,0.5)",
                            }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {dp.scopesNote && (
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 13, color: TEXT_MUTED,
                    lineHeight: 1.6, maxWidth: PROSE_W, margin: "16px auto 0",
                  }}>{dp.scopesNote}</p>
                )}
              </FadeInFast>
            </div>
          </section>
        )}

        {/* Good to know */}
        {detail.goodToKnow && detail.goodToKnow.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "0 20px 56px" : "0 24px 80px" }}>
            <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
              <FadeInFast>
                <h3 style={{
                  fontFamily: "var(--display)", fontSize: 17, fontWeight: 600,
                  color: TEXT, margin: "0 0 14px",
                }}>Good to know</h3>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {detail.goodToKnow.map((item, i) => (
                    <li key={i} style={{
                      fontFamily: "var(--body)", fontSize: 14.5, color: TEXT_MUTED,
                      lineHeight: 1.65, padding: "6px 0 6px 18px", position: "relative",
                    }}>
                      <span style={{
                        position: "absolute", left: 0, top: 14,
                        width: 5, height: 5, borderRadius: "50%",
                        background: "rgba(255,255,255,0.25)",
                      }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </FadeInFast>
            </div>
          </section>
        )}

        {/* Full setup guide link. Optional; only integrations with a dedicated
            documentation page set setup.docsUrl. Rendered as a real anchor
            because a bare URL inside prose is not clickable, and this one is a
            HubSpot listing requirement that reviewers follow. */}
        {detail.setup && detail.setup.docsUrl && (
          <section style={{ background: BG, padding: isMobile ? "0 20px 40px" : "0 24px 56px" }}>
            <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
              <FadeInFast>
                <a
                  href={detail.setup.docsUrl}
                  style={{
                    fontFamily: "var(--body)", fontSize: 15, color: TEAL,
                    textDecoration: "none", borderBottom: `1px solid ${TEAL}40`,
                  }}
                >
                  {detail.setup.docsLabel || "Read the full setup guide"}
                </a>
              </FadeInFast>
            </div>
          </section>
        )}

        {/* Pricing. Optional per integration - only HubSpot defines it today,
            because it is the only one sold as a standalone product with its own
            price rather than as part of a plan. Deliberately in the product-doc
            register (a ladder of rows, one teal price each) rather than a
            pricing-card grid: this page is documentation, and a sales-page
            treatment here would fight the rest of it. */}
        {detail.pricing && detail.pricing.tiers && detail.pricing.tiers.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "0 20px 56px" : "0 24px 80px" }}>
            <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
              <FadeInFast>
                <SectionHeading>Pricing</SectionHeading>
                {detail.pricing.intro && <Prose>{detail.pricing.intro}</Prose>}
                <div style={{
                  border: `1px solid ${CARD_BORDER}`, borderRadius: 12, overflow: "hidden", marginTop: 8,
                }}>
                  {detail.pricing.tiers.map((tier, i) => (
                    <div key={i} style={{
                      padding: isMobile ? "16px 18px" : "18px 22px",
                      borderTop: i === 0 ? "none" : `1px solid ${CARD_BORDER}`,
                    }}>
                      <div style={{
                        display: "flex", alignItems: "baseline", justifyContent: "space-between",
                        gap: 16, marginBottom: 6, flexWrap: "wrap",
                      }}>
                        <span style={{
                          fontFamily: "var(--display)", fontSize: 15.5, fontWeight: 600, color: TEXT,
                        }}>{tier.name}</span>
                        <span style={{
                          fontFamily: "var(--body)", fontSize: 14.5, fontWeight: 600,
                          color: TEAL, whiteSpace: "nowrap",
                        }}>{tier.price}</span>
                      </div>
                      <p style={{
                        fontFamily: "var(--body)", fontSize: 14.5, color: TEXT_MUTED,
                        lineHeight: 1.65, margin: 0,
                      }}>{tier.includes}</p>
                    </div>
                  ))}
                </div>
                {detail.pricing.note && (
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 13.5, color: TEXT_MUTED,
                    lineHeight: 1.65, margin: "14px 0 0",
                  }}>{detail.pricing.note}</p>
                )}
                {detail.pricing.link && (
                  <p style={{ fontFamily: "var(--body)", fontSize: 13.5, margin: "10px 0 0" }}>
                    <a href={detail.pricing.link.href} style={{ color: TEAL, textDecoration: "none", fontWeight: 600 }}>
                      {detail.pricing.link.label} →
                    </a>
                  </p>
                )}
              </FadeInFast>
            </div>
          </section>
        )}

        {/* FAQs */}
        {detail.faqs && detail.faqs.length > 0 && (
          <section style={{ background: BG_ALT, padding: isMobile ? "56px 20px" : "80px 24px" }}>
            <div style={{ maxWidth: PROSE_W, margin: "0 auto" }}>
              <FadeInFast>
                <SectionHeading>Frequently asked questions</SectionHeading>
              </FadeInFast>
              <FaqList faqs={detail.faqs} />
            </div>
          </section>
        )}

        {/* Related integrations */}
        {related.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "56px 20px" : "80px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeInFast>
                <SectionHeading>Related integrations</SectionHeading>
              </FadeInFast>
              <FadeInFast delay={0.06}>
                <ul style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap: 12, listStyle: "none", margin: 0, padding: 0,
                }}>
                  {related.map((rel) => (
                    <li key={rel.slug} style={{ listStyle: "none" }}>
                      <DirectoryCard integration={rel} status={rel.status} />
                    </li>
                  ))}
                </ul>
              </FadeInFast>
            </div>
          </section>
        )}

        {/* CTA */}
        <section style={{ background: BG, padding: isMobile ? "72px 20px" : "120px 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <CtaGridBackground bg={BG} />
          <div style={{ maxWidth: 600, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <FadeInFast>
              <h2 style={{
                fontFamily: "var(--display)", fontWeight: 700,
                fontSize: "clamp(24px, 3.5vw, 40px)", letterSpacing: "-0.037em",
                color: TEXT, marginBottom: 16,
              }}>{detail.ctaClose?.title || `Ready to connect ${integration.name}?`}</h2>
              <p style={{
                fontFamily: "var(--body)", fontSize: 16, color: TEXT_MUTED,
                lineHeight: 1.6, marginBottom: 32,
              }}>
                {detail.ctaClose?.subtitle || "Start your 14-day free trial and connect your tools in minutes."}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <PrimaryBtn href={utm("integrations", "signup", `${integration.slug}_cta`)}>Start Free Trial</PrimaryBtn>
                <SecondaryBtn href="/contact">Talk to us</SecondaryBtn>
              </div>
            </FadeInFast>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
